export type SoundType = 'none' | 'soft' | 'mechanical' | 'digital' | 'nature' | 'zen' | 'retro';

class SoundManager {
  private tickingSound: HTMLAudioElement | null = null;
  private alarmSound: HTMLAudioElement | null = null;
  private currentType: SoundType = 'none';
  private tickVolume: number = 0.5;
  private alarmVolume: number = 0.7;

  constructor() {
    if (typeof window !== 'undefined') {
      this.tickingSound = new Audio();
      this.alarmSound = new Audio();
    }
  }

  setVolume(volume: number) {
    this.tickVolume = volume;
    this.alarmVolume = volume + 0.1; // Alarm slightly louder than tick
    
    if (this.tickingSound) {
      this.tickingSound.volume = this.getAdjustedVolume(this.tickVolume);
    }
    if (this.alarmSound) {
      this.alarmSound.volume = this.getAdjustedVolume(this.alarmVolume);
    }
  }

  private getAdjustedVolume(baseVolume: number): number {
    // Adjust volume based on sound type
    let multiplier = 1;
    switch (this.currentType) {
      case 'nature':
        multiplier = 0.8; // Nature sounds tend to be louder
        break;
      case 'zen':
        multiplier = 0.6; // Zen sounds should be more subtle
        break;
      case 'retro':
        multiplier = 0.9; // Retro sounds at moderate volume
        break;
      default:
        multiplier = 1;
    }
    return Math.min(baseVolume * multiplier, 1);
  }

  setType(type: SoundType) {
    this.currentType = type;
    if (this.tickingSound) {
      switch (type) {
        case 'soft':
          this.tickingSound.src = '/sounds/soft-tick.mp3';
          this.alarmSound!.src = '/sounds/soft-bell.mp3';
          break;
        case 'mechanical':
          this.tickingSound.src = '/sounds/mechanical-tick.mp3';
          this.alarmSound!.src = '/sounds/mechanical-bell.mp3';
          break;
        case 'digital':
          this.tickingSound.src = '/sounds/digital-tick.mp3';
          this.alarmSound!.src = '/sounds/digital-bell.mp3';
          break;
        case 'nature':
          this.tickingSound.src = '/sounds/nature-tick.mp3';
          this.alarmSound!.src = '/sounds/nature-bell.mp3';
          break;
        case 'zen':
          this.tickingSound.src = '/sounds/zen-tick.mp3';
          this.alarmSound!.src = '/sounds/zen-bell.mp3';
          break;
        case 'retro':
          this.tickingSound.src = '/sounds/retro-tick.mp3';
          this.alarmSound!.src = '/sounds/retro-bell.mp3';
          break;
        case 'none':
          this.tickingSound.src = '';
          this.alarmSound!.src = '';
          break;
      }
      // Update volumes when changing sound type
      this.setVolume(this.tickVolume);
    }
  }

  startTicking() {
    if (this.currentType === 'none' || !this.tickingSound) return;
    
    this.tickingSound.loop = true;
    this.tickingSound.volume = this.getAdjustedVolume(this.tickVolume);
    
    this.tickingSound.play().catch(error => {
      console.error('Error playing ticking sound:', error);
    });
  }

  stopTicking() {
    if (this.tickingSound) {
      this.tickingSound.pause();
      this.tickingSound.currentTime = 0;
    }
  }

  playAlarm() {
    if (this.currentType === 'none' || !this.alarmSound) return;
    
    this.alarmSound.volume = this.getAdjustedVolume(this.alarmVolume);
    
    this.alarmSound.play().catch(error => {
      console.error('Error playing alarm sound:', error);
    });
  }
}

export const soundManager = new SoundManager(); 